import React, { useMemo } from 'react';

import { Divider, Paper } from '@mui/material';

import ShowChartIcon from '@mui/icons-material/ShowChartRounded';
import FormatListBulletedRoundedIcon from '@mui/icons-material/FormatListBulletedRounded';

import classes from './InbodyIndicators.module.scss';
import {
  LbTabPanel,
  LbTabs,
  LbToggleTabsList,
  TabsConfig,
} from '../../../../../../shared/components/tabs';
import { InbodyIndicatorsTableView } from './InbodyIndicatorsTableView/InbodyIndicatorsTableView';
import { InbodyIndicatorsChartView } from './InbodyIndicatorsChartView/InbodyIndicatorsChartView';
import { InbodyIndicatorInfo } from '../../../../../interfaces/inbodyIndicator';
import { Option } from '../../../../../../general-types';
import { InbodyIndicatorsOptions } from '../InbodyIndicatorsOptions/InbodyIndicatorsOptions';
import { MealPlanOnScanDayInfo } from '../../../../../interfaces/mealPlanOnScanDayInfo';
import { INBODY_TYPES } from '../../../../../../shared/constants/inbody-info/InbodyType';
import { getFlagsObject } from '../../../../../../shared/utils/sortingAndGroupingUtil';
import { CustomerInfo } from '../NgzInbodyIndicatorsTab.settings';
import { ScanMetadata } from '../../../../../interfaces/scanMetadata';
import { CustomerInfoSection } from './CustomerInfoSection/CustomerInfoSection';
import { FilterSection } from './FilterSection/FilterSection';
import { CustomerType } from '../../../../../../shared/constants/customer/customerType';
import { EmptyList } from '../../../../../../shared/components/ui-components';

const CheckInViewTabs = {
  CHART: 'CHART',
  TABLE: 'TABLE',
} as const;
const { CHART, TABLE } = CheckInViewTabs;

type InbodyIndicatorsProps = {
  customerInbodyIndicators: InbodyIndicatorInfo[];
  mealPlansOnScanDay: MealPlanOnScanDayInfo[];
  inbodyTypeOptions: Option[];
  availableScansMetadata: ScanMetadata[];
  customerInfo: CustomerInfo;
  customerType: CustomerType;
  showAllScans: boolean;
  entityId?: number;
  onToggleShowAllScans: (checked: boolean) => void;
};

const InbodyIndicators = ({
  customerInbodyIndicators,
  mealPlansOnScanDay,
  inbodyTypeOptions,
  availableScansMetadata,
  customerInfo,
  customerType,
  showAllScans,
  entityId,
  onToggleShowAllScans,
}: InbodyIndicatorsProps) => {
  const inbodyTypeFlags = useMemo(
    () => getFlagsObject(INBODY_TYPES, [customerInfo.inbodyType]),
    [customerInfo.inbodyType],
  );

  const tabsConfigs: TabsConfig[] = [
    {
      icon: <ShowChartIcon fontSize="inherit" />,
      value: CHART,
      panel: (
        <InbodyIndicatorsChartView
          customerInbodyIndicators={customerInbodyIndicators}
          mealPlansOnScanDay={mealPlansOnScanDay}
          customerInbodyTypeFlags={inbodyTypeFlags}
        />
      ),
    },
    {
      icon: <FormatListBulletedRoundedIcon fontSize="inherit" />,
      value: TABLE,
      panel: (
        <InbodyIndicatorsTableView customerInbodyIndicators={customerInbodyIndicators} />
      ),
    },
  ];

  const defaultTab = inbodyTypeFlags.NON_INBODY ? TABLE : CHART;

  return (
    <LbTabs defaultValue={defaultTab}>
      <Paper variant="box" className={classes.topBlock}>
        <div className={classes.leftItems}>
          <CustomerInfoSection
            customerInfo={customerInfo}
            inbodyTypeFlags={inbodyTypeFlags}
          />
          <Divider orientation="vertical" flexItem />
          <FilterSection
            inbodyTypeFlags={inbodyTypeFlags}
            inbodyTypeOptions={inbodyTypeOptions}
            availableScansMetadata={availableScansMetadata}
            showAllScans={showAllScans}
            onToggleShowAllScans={onToggleShowAllScans}
          />
        </div>
        <div className={classes.rightItems}>
          <LbToggleTabsList
            className={classes.tabsList}
            tabsConfig={tabsConfigs}
            size="small"
          />
          <InbodyIndicatorsOptions
            customerInfo={customerInfo}
            availableScansMetadata={availableScansMetadata}
            customerInbodyIndicators={customerInbodyIndicators}
            customerInbodyTypeFlags={inbodyTypeFlags}
            customerType={customerType}
            entityId={entityId}
          />
        </div>
      </Paper>
      {customerInbodyIndicators.length > 0 ? (
        <LbTabPanel tabsConfig={tabsConfigs} />
      ) : (
        <EmptyList show description="There are no scans" />
      )}
    </LbTabs>
  );
};

export { InbodyIndicators };
